import {
  defaultBusinessDaysWindow,
  maxBusinessDaysInMonth,
  nthBusinessDayOfMonth,
  parseISODate,
  toISODate,
} from './feriadosChile'

/** Meses de la ventana móvil (alineado con Inicio / lambda). */
export const ROLLING_WINDOW_MONTHS = 6

const MES_LABEL = [
  '',
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
]

/** Etiquetas para montos sin match en líneas/metas (antes quedaban fuera de los filtros). */
export const SIN_EQUIPO = 'Otros'
export const SIN_EJECUTIVO = 'Otros'

const emptyMetrics = () => ({
  cedIf: 0,
  cedOtro: 0,
  facturacion: 0,
  cesion: 0,
})

export function labelMes(month) {
  return MES_LABEL[month] || String(month)
}

export function normalizeEquipo(value) {
  const s = String(value || '').trim()
  return s || SIN_EQUIPO
}

export function normalizeEjecutivo(value) {
  const s = String(value || '').trim()
  return s || SIN_EJECUTIVO
}

export function uniqueSortedLabels(values) {
  return [...new Set(values.map((v) => String(v || '').trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, 'es', { sensitivity: 'base' }),
  )
}

/** Opciones de filtro; incluye «Otros» (vacíos) al final. */
export function uniqueDimensionLabels(values, { emptyLabel }) {
  const set = new Set((values || []).map((v) => {
    const s = String(v || '').trim()
    return s || emptyLabel
  }))
  const list = [...set].sort((a, b) => {
    if (a === emptyLabel) return 1
    if (b === emptyLabel) return -1
    return a.localeCompare(b, 'es', { sensitivity: 'base' })
  })
  return list
}

/**
 * Agrega filas (FECHA + dimensiones) a serie diaria.
 * filters.equipos / filters.ejecutivos vacíos = sin filtro (totales globales).
 * Valores vacíos se tratan como «Otros» para que filtrar por todos los
 * equipos (incl. Otros) cuadre con el total sin filtros.
 */
export function aggregateDailySeries(filas, filters = {}) {
  const equipos = new Set((filters.equipos || []).map((x) => String(x).trim()).filter(Boolean))
  const ejecutivos = new Set(
    (filters.ejecutivos || []).map((x) => String(x).trim()).filter(Boolean),
  )
  const byDate = new Map()

  for (const row of filas || []) {
    const fecha = String(row.FECHA || '').slice(0, 10)
    if (!fecha) continue
    const equipo = normalizeEquipo(row.EQUIPO_VENTAS)
    const ejecutivo = normalizeEjecutivo(row.EJECUTIVO)
    if (equipos.size && !equipos.has(equipo)) continue
    if (ejecutivos.size && !ejecutivos.has(ejecutivo)) continue

    const cur = byDate.get(fecha) || emptyMetrics()
    const cedIf = Number(row.CED_IF) || 0
    const cedOtro = Number(row.CED_OTRO) || 0
    const cesionRaw = row.CESION
    const cesion =
      cesionRaw != null && cesionRaw !== ''
        ? Number(cesionRaw) || 0
        : cedIf + cedOtro
    cur.cedIf += cedIf
    cur.cedOtro += cedOtro
    cur.facturacion += Number(row.FACTURACION) || 0
    cur.cesion += cesion
    byDate.set(fecha, cur)
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([fecha, m]) => enrichDayRow(fecha, m))
}

function enrichDayRow(fecha, m) {
  const cedIf = m.cedIf
  const cedOtro = m.cedOtro
  const cesion = m.cesion || cedIf + cedOtro
  const facturacion = m.facturacion
  const partIf = cedIf + cedOtro > 0 ? cedIf / (cedIf + cedOtro) : 0
  const cesFact = facturacion > 0 ? cesion / facturacion : 0
  return {
    fecha,
    cedIf,
    cedOtro,
    participacionIf: partIf,
    facturacion,
    cesion,
    cesionesSobreFacturacion: cesFact,
  }
}

function sumThrough(daily, fromISO, toISO) {
  const acc = emptyMetrics()
  for (const row of daily) {
    if (row.fecha < fromISO || row.fecha > toISO) continue
    acc.cedIf += row.cedIf
    acc.cedOtro += row.cedOtro
    acc.facturacion += row.facturacion
    acc.cesion += row.cesion
  }
  return enrichDayRow(toISO, acc)
}

function subtractMonths(date, months) {
  const y = date.getFullYear()
  const m = date.getMonth()
  const d = date.getDate()
  const target = new Date(y, m - months, 1)
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  return new Date(target.getFullYear(), target.getMonth(), Math.min(d, lastDay))
}

/** Primer día del mes más antiguo de la ventana (6 meses calendario). */
export function rollingWindowStartISO(fechaCorteISO, months = ROLLING_WINDOW_MONTHS) {
  const corte = parseISODate(fechaCorteISO)
  if (!corte) return null
  const oldest = subtractMonths(corte, months - 1)
  return toISODate(new Date(oldest.getFullYear(), oldest.getMonth(), 1))
}

/** Exactamente N meses calendario terminando en el mes de fecha_corte. */
export function monthsInRollingWindow(fechaCorteISO, months = ROLLING_WINDOW_MONTHS) {
  const corte = parseISODate(fechaCorteISO)
  if (!corte) return []
  const out = []
  for (let i = months - 1; i >= 0; i -= 1) {
    const d = subtractMonths(corte, i)
    out.push({ year: d.getFullYear(), month: d.getMonth() + 1 })
  }
  return out
}

function monthHasData(metrics) {
  return (
    (metrics.cedIf || 0) +
      (metrics.cedOtro || 0) +
      (metrics.facturacion || 0) +
      (metrics.cesion || 0) >
    0
  )
}

function monthLabel(year, month) {
  return `${labelMes(month)} ${year}`
}

function lastDayOfMonthISO(year, month) {
  return toISODate(new Date(year, month, 0))
}

export function closedMonthsThrough(fechaCorteISO) {
  const corte = parseISODate(fechaCorteISO)
  if (!corte) return { year: null, lastClosedMonth: 0 }
  const year = corte.getFullYear()
  const mesCorte = corte.getMonth() + 1
  const ultimoDiaMesCorte = lastDayOfMonthISO(year, mesCorte)
  const mesCorteCerrado = fechaCorteISO === ultimoDiaMesCorte
  const lastClosedMonth = mesCorteCerrado ? mesCorte : mesCorte - 1
  return { year, lastClosedMonth, mesCorte, mesCorteCerrado }
}

/**
 * Meses calendario cerrados completos (día 1 → último día del mes),
 * solo dentro de la ventana móvil de N meses.
 */
export function buildMesesCerradosCompletos(daily, fechaCorteISO) {
  if (!parseISODate(fechaCorteISO)) return []
  const rows = []
  for (const { year: y, month } of monthsInRollingWindow(fechaCorteISO)) {
    const fromISO = `${y}-${String(month).padStart(2, '0')}-01`
    const hasta = lastDayOfMonthISO(y, month)
    if (hasta > fechaCorteISO) continue
    const metrics = sumThrough(daily, fromISO, hasta)
    if (!monthHasData(metrics)) continue
    rows.push({
      anio: y,
      mes: month,
      label: monthLabel(y, month),
      desde: fromISO,
      hasta,
      ...metrics,
    })
  }
  return rows
}

/**
 * Bloque "Avance N días hábiles": una fila por mes dentro de la ventana móvil.
 */
export function buildAvancePorMes(daily, fechaCorteISO, nHabiles) {
  const corte = parseISODate(fechaCorteISO)
  if (!corte) return []
  const n = Math.max(1, Math.floor(Number(nHabiles) || 1))
  const mesCorte = corte.getMonth() + 1
  const anioCorte = corte.getFullYear()
  const rows = []

  for (const { year, month } of monthsInRollingWindow(fechaCorteISO)) {
    const endDate = nthBusinessDayOfMonth(year, month, n)
    if (!endDate) continue
    let endISO = toISODate(endDate)
    if (year === anioCorte && month === mesCorte && endISO > fechaCorteISO) {
      endISO = fechaCorteISO
    }
    const fromISO = `${year}-${String(month).padStart(2, '0')}-01`
    const metrics = sumThrough(daily, fromISO, endISO)
    if (!monthHasData(metrics)) continue
    rows.push({
      anio: year,
      mes: month,
      label: monthLabel(year, month),
      desde: fromISO,
      hasta: endISO,
      ...metrics,
    })
  }
  return rows
}

export function variacionPct(actual, previo) {
  const a = Number(actual)
  const p = Number(previo)
  if (!Number.isFinite(a) || !Number.isFinite(p) || p === 0) return null
  return (a - p) / p
}

export function buildVariacionUltimosMeses(avanceRows) {
  if (!avanceRows || avanceRows.length < 2) return null
  const prev = avanceRows[avanceRows.length - 2]
  const curr = avanceRows[avanceRows.length - 1]
  return {
    label: `Variación % ${labelMes(prev.mes)}-${labelMes(curr.mes)}`,
    cedIf: variacionPct(curr.cedIf, prev.cedIf),
    cedOtro: variacionPct(curr.cedOtro, prev.cedOtro),
    facturacion: variacionPct(curr.facturacion, prev.facturacion),
    cesion: variacionPct(curr.cesion, prev.cesion),
  }
}

export function resolveWindowN(fechaCorteISO, requestedN) {
  const maxN = maxBusinessDaysInMonth(fechaCorteISO)
  const def = defaultBusinessDaysWindow(fechaCorteISO)
  if (requestedN == null || requestedN === '') return Math.min(def, maxN)
  const n = Math.floor(Number(requestedN))
  if (!Number.isFinite(n) || n < 1) return Math.min(def, maxN)
  return Math.min(n, maxN)
}

export { defaultBusinessDaysWindow, maxBusinessDaysInMonth }
